/**
 * Low-level chart primitives shared by the Insights chart components
 * (LineChart, BarChart, StackedBarChart): CSS-custom-property colour resolution
 * and x-axis date-tick formatting, plus the axis sizing constants they render
 * with. Framework helpers only (no recharts imports) so each chart can compose
 * them without depending on the others.
 */

/** Y-axis tick count; keep in sync with formatYAxisTick's TICK_COUNT. */
export const Y_TICK_COUNT = 5
export const TICK_FONT_SIZE = 12

// Y-axis band-width sizing, shared with the new charts so the two chart systems
// never drift. @see ../charts/yAxisWidth
export {
  axisTickLabels,
  computeYAxisWidth,
  Y_AXIS_LABEL_GAP,
  Y_AXIS_LABEL_OFFSET,
  Y_AXIS_TICK_MARGIN,
  Y_AXIS_WIDTH,
} from '../charts/yAxisWidth'

const ONE_DAY_MS = 86_400_000

/**
 * Resolve a CSS custom-property reference (a `var(…)` value) to its computed
 * result so recharts — which paints to SVG and can't read CSS variables — gets
 * a concrete colour. Non-`var()` values pass through unchanged.
 */
export function resolveColor(cssVar: string): string {
  if (!cssVar.startsWith('var(')) return cssVar
  const propName = cssVar.slice(4, -1).trim()
  return getComputedStyle(document.documentElement)
    .getPropertyValue(propName)
    .trim()
}

/**
 * Build an x-axis tick formatter for day-resolution timestamps. Adds the year
 * only when the range spans calendar years, and drops the day for
 * monthly-bucketed data (where a day-of-month reads as noise). Formatting is in
 * UTC to match how API points are stored (00:00:00Z per day).
 */
export function makeFormatDateTick(
  startDate?: Date,
  endDate?: Date,
  monthly = false,
): (timestamp: number) => string {
  const spansYears =
    startDate != null &&
    endDate != null &&
    startDate.getFullYear() !== endDate.getFullYear()
  return (timestamp) =>
    new Date(timestamp).toLocaleDateString('en-US', {
      month: 'short',
      ...(monthly ? {} : { day: 'numeric' }),
      ...(spansYears ? { year: 'numeric' } : {}),
      timeZone: 'UTC',
    })
}

/**
 * Month tick label for monthly-bucketed bar axes. Defaults to short month +
 * full year (`Jul 2026`), which reads unambiguously as a month. Pass
 * `compact` for wide views (many months, less room per tick) to use an
 * apostrophe-prefixed 2-digit year (`Jul '26`) — the apostrophe keeps it from
 * reading like a day-of-month. UTC to match how points are stored.
 */
export function makeMonthTickFormatter(
  compact = false,
): (timestamp: number) => string {
  return (timestamp) => {
    const date = new Date(timestamp)
    const month = date.toLocaleDateString('en-US', {
      month: 'short',
      timeZone: 'UTC',
    })
    if (!compact) {
      const year = date.toLocaleDateString('en-US', {
        year: 'numeric',
        timeZone: 'UTC',
      })
      return `${month} ${year}`
    }
    const year = date.toLocaleDateString('en-US', {
      year: '2-digit',
      timeZone: 'UTC',
    })
    return `${month} '${year}`
  }
}

/**
 * Compact week tick label — the bucket's Monday through Sunday, e.g. `Jun 1 - 7`
 * (or `Jun 29 - Jul 5` across a month boundary), dropping the repeated month and
 * the year to stay compact on the axis. The tooltip carries the full range +
 * year. UTC to match how points are stored.
 */
export function makeWeekTickFormatter(): (timestamp: number) => string {
  return (timestamp) => {
    const start = new Date(timestamp)
    const end = new Date(timestamp + 6 * ONE_DAY_MS)
    const startStr = start.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    })
    const sameMonth = start.getUTCMonth() === end.getUTCMonth()
    const endStr = end.toLocaleDateString('en-US', {
      ...(sameMonth ? {} : { month: 'short' }),
      day: 'numeric',
      timeZone: 'UTC',
    })
    return `${startStr} - ${endStr}`
  }
}
